import { Link } from '@tanstack/react-router'
import { Ticket } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { AvailabilityBadge, ConfiguredLimitBadge, PriceOrDiscountBlock, ServiceStatusBadge } from '@/shared/components/cse'
import { CatalogCardBody, CatalogCardFooter } from '@/shared/components/CatalogCardLayout'
import { Button } from '@/shared/components/ui/button'
import { PictureImg } from '@/shared/components/ui/picture-img'
import { formatMoneyCents } from '@/shared/lib/formatMoney'
import { resolveAssetUrl } from '@/shared/lib/resolveAssetUrl'
import type { TicketingOffer } from '@/features/ticketing/schemas/ticketing.schema'
import { categoryDisplayLabel } from '@/features/ticketing/utils/categoryDisplayLabel'

function displayValue(value: string | number | null | undefined): string | null {
  if (value === null || value === undefined || value === '') {
    return null
  }

  return String(value)
}

export function HorizonTicketCard({ offer, priority = false }: { offer: TicketingOffer; priority?: boolean }) {
  const { t } = useTranslation()
  const detailTo = `/ticketing/offers/${encodeURIComponent(offer.id)}`
  const price =
    offer.price_cents != null
      ? formatMoneyCents(offer.price_cents)
      : offer.price != null
        ? String(offer.price)
        : t('ticketing.list.price_pending')
  const compareAtPrice =
    offer.original_price_cents != null ? formatMoneyCents(offer.original_price_cents) : undefined
  const discount = displayValue(offer.discount)
  const stock = displayValue(offer.stock)
  const category = categoryDisplayLabel(offer)
  const mediaUrl = offer.thumb_url ?? offer.image_url
  const mediaWebp = offer.thumb_webp_url ?? offer.image_webp_url

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-[var(--brand-radius-card)] bg-[var(--brand-background)]">
      <div className="relative aspect-[4/3] overflow-hidden rounded-[var(--brand-radius-card)] bg-[var(--brand-surface-strong)]">
        <Link to={detailTo} tabIndex={-1} aria-hidden className="block size-full focus:outline-none">
          {mediaUrl ? (
            <PictureImg
              webpSrc={mediaWebp ? resolveAssetUrl(mediaWebp) : undefined}
              src={resolveAssetUrl(mediaUrl)}
              alt=""
              aria-hidden="true"
              loading={priority ? 'eager' : 'lazy'}
              decoding="async"
              className="size-full object-cover transition duration-200 group-hover:scale-[1.02]"
              sizes="(min-width: 1280px) 360px, (min-width: 640px) 50vw, 100vw"
              width={640}
              height={480}
            />
          ) : (
            <div className="flex size-full items-center justify-center text-[var(--brand-primary)]">
              <Ticket className="size-10" aria-hidden />
            </div>
          )}
        </Link>
        <div className="absolute left-3 top-3 flex flex-wrap gap-2">
          {discount ? (
            <span className="inline-flex min-h-8 items-center rounded-[var(--brand-radius-pill)] bg-[var(--brand-background)]/95 px-3 text-xs font-semibold text-[var(--brand-text-primary)] shadow-[var(--brand-shadow-1)]">
              {t('ticketing.list.discount_badge', { discount })}
            </span>
          ) : null}
          <ServiceStatusBadge label={t('ticketing.status.published')} tone="open" />
        </div>
      </div>

      <CatalogCardBody>
        <h3 className="m-0 text-base font-semibold leading-snug text-[var(--brand-text-primary)]">
          <Link
            to={detailTo}
            className="line-clamp-2 text-inherit no-underline hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-ring)]"
          >
            {offer.title}
          </Link>
        </h3>
        <p className="text-sm text-[var(--brand-text-secondary)]">
          {category ? t('ticketing.list.category', { category }) : t('ticketing.list.category_default')}
        </p>
        <div className="flex flex-wrap gap-2">
          <AvailabilityBadge label={stock ? t('ticketing.list.stock', { stock }) : t('ticketing.list.stock_configured')} available />
          <ConfiguredLimitBadge label={t(`ticketing.mode.${offer.mode}`, { defaultValue: offer.mode })} />
        </div>
        <CatalogCardFooter className="border-[var(--brand-border-soft)]">
          <PriceOrDiscountBlock
            bare
            label={discount ? t('ticketing.list.discount_label') : undefined}
            value={discount ?? price}
            compareAt={discount ? undefined : compareAtPrice}
            helper={t('ticketing.list.price_helper')}
          />
          <Button size="sm" variant="outline" asChild>
            <Link to={detailTo} aria-label={t('ticketing.list.view_aria', { title: offer.title })}>
              {t('ticketing.list.view')}
            </Link>
          </Button>
        </CatalogCardFooter>
      </CatalogCardBody>
    </article>
  )
}
